import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Descriptions, Button, message, Typography, Spin } from 'antd';
import { CopyOutlined, ProjectOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar } from 'antd';
import type { FreePhotoMetadata } from '../utils/freePhotoUtils';
import { formatCompassHeading, formatCoordinates } from '../utils/freePhotoUtils';
import DateTimeViewer from '~/components/common/DateTimeViewer/DateTimeViewer';
import { remote_getProjectById } from '~/common/typescript/apis/ProjectAPIs';
import { useUserById } from '../hooks/useUserById';

const { Text } = Typography;

interface PhotoMetadataPanelProps {
  metadata: FreePhotoMetadata | null;
  fileName: string;
  createdAt?: string;
  projectId?: number;
  userId?: number;
}

/**
 * Metadata panel component for displaying photo information.
 * - Uses DateTimeViewer for relative time display (click to toggle).
 * - Fetches project name by ID on mount.
 */
export const PhotoMetadataPanel: React.FC<PhotoMetadataPanelProps> = ({ metadata, fileName, createdAt, projectId, userId }) => {
  const [projectName, setProjectName] = useState<string | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const { user, loading: userLoading } = useUserById(userId);

  // Fetch project name when projectId changes
  useEffect(() => {
    if (!projectId) {
      setProjectName(null);
      return;
    }
    setProjectLoading(true);
    remote_getProjectById(projectId)
      .then((proj) => setProjectName(proj.name ?? `ID: ${projectId}`))
      .catch(() => setProjectName(`ID: ${projectId}`))
      .finally(() => setProjectLoading(false));
  }, [projectId]);

  // location follows GeoPosition: location.coords.latitude
  const lat = metadata?.location?.coords?.latitude;
  const lng = metadata?.location?.coords?.longitude;
  const accuracy = metadata?.location?.coords?.accuracy;
  const altitude = metadata?.location?.coords?.altitude;
  const hasCoords = lat != null && lng != null;

  const handleCopyCoordinates = () => {
    if (hasCoords) {
      navigator.clipboard.writeText(formatCoordinates(lat, lng));
      message.success('Đã sao chép tọa độ');
    }
  };

  return (
    <UI_Wrapper>
      <Descriptions column={1} bordered size="small">
        {createdAt && (
          <Descriptions.Item label="Thời gian chụp">
            <DateTimeViewer date={createdAt} initialMode="relative" showSeconds />
          </Descriptions.Item>
        )}

        {userId && (
          <Descriptions.Item label="Người chụp">
            {userLoading ? (
              <Spin size="small" />
            ) : user ? (
              <UI_UserRow>
                <Avatar
                  size={24}
                  src={user.avatar && typeof user.avatar === 'object' && 'url' in user.avatar ? (user.avatar as any).url : undefined}
                  icon={<UserOutlined />}
                />
                <span>{user.fullname || user.username}</span>
              </UI_UserRow>
            ) : (
              <span style={{ color: '#999' }}>ID: {userId}</span>
            )}
          </Descriptions.Item>
        )}

        {projectId && (
          <Descriptions.Item label="Dự án">
            {projectLoading ? (
              <Spin size="small" />
            ) : (
              <UI_ProjectName>
                <ProjectOutlined />
                <span>{projectName ?? `ID: ${projectId}`}</span>
              </UI_ProjectName>
            )}
          </Descriptions.Item>
        )}

        {hasCoords && (
          <>
            <Descriptions.Item label="Tọa độ GPS">
              <UI_CoordinatesWrapper>
                <Text>{formatCoordinates(lat, lng)}</Text>
                <Button type="text" size="small" icon={<CopyOutlined />} onClick={handleCopyCoordinates} />
              </UI_CoordinatesWrapper>
            </Descriptions.Item>

            {accuracy != null && (
              <Descriptions.Item label="Độ chính xác">
                {Number(accuracy).toFixed(1)} mét
              </Descriptions.Item>
            )}

            {altitude != null && (
              <Descriptions.Item label="Độ cao">
                {Number(altitude).toFixed(1)} mét
              </Descriptions.Item>
            )}
          </>
        )}

        {metadata?.compassHeading !== undefined && (
          <Descriptions.Item label="Hướng chụp">
            {formatCompassHeading(metadata.compassHeading)}
          </Descriptions.Item>
        )}

        {metadata?.address && (
          <Descriptions.Item label="Địa chỉ">
            {metadata.address}
          </Descriptions.Item>
        )}

        {metadata?.deviceInfo && (
          <>
            {metadata.deviceInfo.model && (
              <Descriptions.Item label="Thiết bị">
                {metadata.deviceInfo.model}
              </Descriptions.Item>
            )}
            {metadata.deviceInfo.os && (
              <Descriptions.Item label="Hệ điều hành">
                {metadata.deviceInfo.os}
              </Descriptions.Item>
            )}
          </>
        )}
      </Descriptions>
    </UI_Wrapper>
  );
};

const UI_Wrapper = styled.div`
  padding: 16px;
  background-color: #fff;
  height: 100%;
  overflow-y: auto;
`;

const UI_CoordinatesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UI_ProjectName = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #1677ff;
`;

const UI_UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;